import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Paper, FormControlLabel, Checkbox, Tooltip, Typography, Skeleton, Stack, Grid } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { FieldValues, useFieldArray, useForm, Control } from "react-hook-form";
import { toast } from "react-toastify";
import agent from "../../../app/api/agent";
import AutocompleteInputOption from "../../../app/models/autocompleteInputOption";
import { Order, PaymentOrderState } from "../../../app/models/order";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import { setAdminOrder } from "./adminOrdersSlice";
import { adminOrderValidationSchema } from "./adminOrderValidation";
import AppBreadcrumbs from "../../../app/components/AppBreadcrumbs";
import AppReturn from "../../../app/components/AppReturn";
import dayjs from "dayjs";
import { orderFormCalculateSum, states } from "../../../app/util/util";
import { OrderFormValues } from "../../../app/models/orderFormValues";
import OrderFormBasicInformation from "../../../app/components/Order/OrderFormBasicInformation";
import OrderFormPayerData from "../../../app/components/Order/OrderFormPayerData";
import OrderFormBenefitients from "../../../app/components/Order/OrderFormBenefitients";
import OrderValueBox from "../../../app/components/Order/OrderValueBox";
import OrderFormEducationEffectsModal from "../../../app/components/Order/OrderFormEducationEffectsModal";
import { setClientOrder } from "../../client/orders/clientOrdersSlice";
import { useServicePriceFetching } from "../../../app/hooks/useServicePriceFetching";
import DraftRestoreButton from "../../../app/components/Draft/DraftRestoreButton";
import { useEnhancedFormDraft } from "../../../app/hooks/useEnhancedFormDraft";
import { useEducationEffects } from "../../../app/hooks/useEducationEffects";
import AppUnsavedChangesDialog from "../../../app/components/AppUnsavedChangesDialog";
import { useBlockNavigation } from "../../../app/hooks/useBlockNavigation";
import DraftRestoreDialog from "../../../app/components/Draft/DraftRestoreDialog";
import AppNumberInput from "../../../app/components/AppNumberInput";

interface Props {
    order?: Order;
    copyFromOrder?: Order;
    cancelEdit: () => void;
}

export default function AdminOrderForm({ order, copyFromOrder, cancelEdit }: Props) {
    const { control, reset, handleSubmit, setValue, watch, getValues,
        formState: { errors, isDirty, isSubmitting } } = useForm<OrderFormValues>({
            resolver: yupResolver(adminOrderValidationSchema),
            defaultValues: {
                serviceId: 0,
                customeClientServiceName: '',
                preposedDateTime: '',
                placeOfServiceAndAnotherInformations: '',
                payerInstitutionName: '',
                payerLastName: '',
                payerFirstName: '',
                payerStreet: '',
                payerBuilding: '',
                payerZip: '',
                payerCity: '',
                payerState: '',
                payerCountry: '',
                payerNIP: '',
                rows: [],
                paymentOrderState: PaymentOrderState.PENDING,
                unitDiscountedPrice: 0,
                generateAITest: false // Default to unchecked
            }
        });

    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.account.user);
    const isSuperAdmin = !!(user && user.roles?.includes('SuperAdmin'));
    const [possibleServices, setPossibleServices] = useState<AutocompleteInputOption[]>([]);
    const serviceId = watch('serviceId');
    const generateAITest = watch('generateAITest'); // Watch checkbox state from form
    const watchedUnitDiscountedPrice = watch('unitDiscountedPrice');
    const [manualServiceChange, setManualServiceChange] = useState(false);
    const [unitDiscountedPrice, setUnitDiscountedPrice] = useState<number>(0);
    const [sum, setSum] = useState<number>(0);
    const [totalItemRows, setTotalItemRows] = useState(0);
    const [isLoading, setIsLoading] = useState<boolean>(!!order || !!copyFromOrder); // Show loading if editing or copying

    // State for unsaved changes dialog
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    // State to store proceed and cancel functions from the blocker
    const [proceedNavigationFn, setProceedNavigationFn] = useState<() => void>(() => { });
    const [cancelNavigationFn, setCancelNavigationFn] = useState<() => void>(() => { });
    // State for manual navigation (cancel button)
    const [pendingManualNavigation, setPendingManualNavigation] = useState<(() => void) | null>(null);

    // Draft restore dialog state
    const [showDraftRestoreDialog, setShowDraftRestoreDialog] = useState(false);

    // Track original education effects for change detection
    const [originalEducationEffects, setOriginalEducationEffects] = useState<string | undefined>(undefined);

    // Helper to check if education effects have actual content
    const hasValidEducationEffects = (effects: string | undefined): boolean => {
        if (!effects) return false;
        const cleaned = effects.trim().replace(/;+/g, '').trim();
        return cleaned.length > 0;
    };

    // Helper to check if order has valid questions JSON
    const hasValidQuestionsJson = (questionsJson: string | undefined): boolean => {
        if (!questionsJson) return false;
        try {
            const parsed = JSON.parse(questionsJson);
            if (Array.isArray(parsed)) return parsed.length > 0;
            if (typeof parsed === 'object') return Object.keys(parsed).length > 0;
            return false;
        } catch {
            return false;
        }
    };

    // Watch education effects for checkbox state
    const watchedEducationEffects = watch('educationEffects');
    const orderHasQuestions = order && hasValidQuestionsJson(order.questionsJson);
    
    // Check if education effects have changed (for editing existing orders)
    const educationEffectsChanged = order && originalEducationEffects !== undefined 
        && watchedEducationEffects !== originalEducationEffects;
    
    // Can generate AI: 
    // - For new orders: just need valid effects
    // - For existing orders without questions: can generate anytime with valid effects
    // - For existing orders with questions: effects must have changed
    const canGenerateAI = hasValidEducationEffects(watchedEducationEffects) && 
        (!order || !orderHasQuestions || educationEffectsChanged);

    // Determine checkbox disabled state and tooltip
    const getAICheckboxState = () => {
        if (!hasValidEducationEffects(watchedEducationEffects)) {
            return { disabled: true, tooltip: 'Uzupełnij efekty kształcenia' };
        }
        if (order && orderHasQuestions && !educationEffectsChanged) {
            return { disabled: true, tooltip: 'Zmień efekty kształcenia, aby wygenerować nowe pytania' };
        }
        return { disabled: false, tooltip: '' };
    };

    const aiCheckboxState = getAICheckboxState();

    const handleSetTotalItemRows = (count: number) => {
        setTotalItemRows(count);
        setSum(orderFormCalculateSum(count, unitDiscountedPrice));
    }

    const formType = 'adminOrder';
    const formId = 'new'; // Use 'new' for new forms, disable draft for existing orders

    const fieldsToExcludeFromDraft = [
        'paymentOrderState'
    ];

    // Use our enhanced form draft hook, only for new forms
    const {
        storedDraft,
        isRestoringDraft,
        draftRestorationRef,
        shouldFetchPrice,
        restoredServiceId,
        setShouldFetchPrice,
        setRestoredServiceId,
        handleRestoreDraft,
        clearDraft,
        getDraftSummary,
        draftShownOnLoad,
        draftDeleteCountdown,
        resetCountdown,
        getDraft,
        setDraftDeleteCountdown
    } = useEnhancedFormDraft<OrderFormValues>(
        // Only use draft when creating a new form, not editing
        order ? '' : formType,
        order ? '' : formId,
        { reset, setValue, getValues, isDirty },
        {
            excludeFields: fieldsToExcludeFromDraft,
        }
    );

    // Get draft summary for dialog
    const draftSummary = storedDraft ? getDraftSummary(storedDraft) : { draftDate: '', fields: [] };

    // Check for drafts on component mount
    useEffect(() => {
        // Just make sure we've checked for drafts
        if (!storedDraft && getDraft && !order) {
            const existingDraft = getDraft();
            if (existingDraft) {
                console.log('Found draft on component mount');
                // The useEnhancedFormDraft hook will handle state updating
            }
        }
    }, [getDraft, storedDraft, order]);

    // Show draft restore dialog when a draft is found on load
    useEffect(() => {
        if (!order && storedDraft && draftShownOnLoad) {
            setShowDraftRestoreDialog(true);
        }
    }, [storedDraft, draftShownOnLoad, order]);

    // Use route blocker to prevent leaving with unsaved changes
    useBlockNavigation(
        isDirty,
        (proceed, cancel) => {
            // Store the navigation functions
            setProceedNavigationFn(() => proceed);
            setCancelNavigationFn(() => cancel);
            // Show the confirmation dialog
            setShowUnsavedChangesDialog(true);
        }
    );

    // Use admin-specific education effects hook
    const {
        educationEffects,
        setEducationEffects,
        openModal,
        handleOpenModal,
        handleCloseModal,
        addNewEffect,
        updateEffect,
        removeEffect,
        handleSaveEducationEffects
    } = useEducationEffects({
        serviceId,
        manualServiceChange,
        setValue,
        watch,
        isRestoringDraft,
        draftRestorationRef,
        role: 'admin'
    });

    // Use service price fetching hook
    useServicePriceFetching({
        serviceId,
        isRestoringDraft,
        shouldFetchPrice,
        restoredServiceId,
        order,
        setShouldFetchPrice,
        setRestoredServiceId,
        setUnitDiscountedPrice,
        draftRestorationRef,
        userRole: 'admin'  // Specify admin role
    });

    // Effect to sync service price to form when fetched (from hook)
    // Only runs when service price is first loaded and form field is empty/zero
    useEffect(() => {
        // Ustaw cenę z usługi tylko gdy formularz jest pusty/zero i mamy cenę z usługi
        // Nie nadpisuj jeśli użytkownik już coś wpisał (nawet jeśli to pusty string podczas edycji)
        if (unitDiscountedPrice > 0 && (watchedUnitDiscountedPrice === 0 || watchedUnitDiscountedPrice === undefined)) {
            setValue('unitDiscountedPrice', unitDiscountedPrice, { shouldDirty: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitDiscountedPrice, setValue]); // Celowo pomijamy watchedUnitDiscountedPrice żeby nie resetować podczas edycji

    // Effect to handle price changes and recalculate sum
    useEffect(() => {
        // Zaokrąglij ceny do 2 miejsc po przecinku - pozwól na pusty string (użytkownik kasuje)
        const priceValue = typeof watchedUnitDiscountedPrice === 'number' && !isNaN(watchedUnitDiscountedPrice)
            ? Math.round(watchedUnitDiscountedPrice * 100) / 100 
            : 0;
        
        // Aktualizuj state ceny dla OrderValueBox (pierwszy efekt nie zresetuje formularza gdy priceValue=0)
        setUnitDiscountedPrice(priceValue);
        
        // Wylicz i zaktualizuj sumę
        if (totalItemRows > 0) {
            const newSum = orderFormCalculateSum(totalItemRows, priceValue);
            setSum(newSum);
        }
    }, [watchedUnitDiscountedPrice, totalItemRows]);

    // Set up field array for beneficiaries
    const { fields, append, remove } = useFieldArray({
        control,
        name: "rows"
    });

    // Function to handle cancel with unsaved changes check
    const handleCancel = useCallback(() => {
        if (isDirty) {
            // For manual navigation (buttons), we use a different approach
            setShowUnsavedChangesDialog(true);
            setPendingManualNavigation(() => {
                return () => {
                    // Don't clear drafts - this preserves the draft when navigating away
                    cancelEdit();
                };
            });
        } else {
            // If form is not dirty, we can navigate away directly
            // Also don't clear drafts here
            cancelEdit();
        }
    }, [isDirty, cancelEdit]);

    const handleConfirmNavigation = useCallback(() => {
        setShowUnsavedChangesDialog(false);

        // Handle navigation based on source:
        // 1. If it's a router-based navigation
        if (proceedNavigationFn) {
            proceedNavigationFn();
        }

        // 2. If it's a manual navigation (from cancel button)
        if (pendingManualNavigation) {
            pendingManualNavigation();
            setPendingManualNavigation(null);
        }
    }, [proceedNavigationFn, pendingManualNavigation]);

    // Function to cancel navigation away (stay on page)
    const handleCancelNavigation = useCallback(() => {
        setShowUnsavedChangesDialog(false);

        // If it's a router-based navigation
        if (cancelNavigationFn) {
            cancelNavigationFn();
        }

        // For manual navigation, just close the dialog
        setPendingManualNavigation(null);
    }, [cancelNavigationFn]);

    // Prevent accidental window close or navigation away
    useEffect(() => {
        // Only add the event listener if the form has unsaved changes
        if (isDirty) {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                e.preventDefault();
                e.returnValue = ''; // Chrome requires returnValue to be set
                return '';
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    }, [isDirty]);

    // Load possible services
    useEffect(() => {
        agent.Services.fetchPossibleServices()
            .then(result => setPossibleServices(result))
            .catch(error => console.log(error));
    }, []);

    // Load existing order data if editing
    useEffect(() => {
        if (order && !isDirty) {
            const rows = (order?.benefitients ?? '')
                .split(';')
                .filter(item => item.trim() !== '') // Filter out empty strings
                .map(item => {
                    const [firstName, lastName, pesel, birthPlace, birthDate, certId] = item.split('~');
                    return { firstName, lastName, pesel, birthPlace, birthDate, certId };
                });
            reset({ ...order, rows, preposedDateTime: dayjs(order.preposedDateTime).toISOString() });
            
            // Ustaw ceny z zamówienia
            if (order.unitDiscountedPrice) {
                setValue('unitDiscountedPrice', order.unitDiscountedPrice, { shouldValidate: true });
                setUnitDiscountedPrice(order.unitDiscountedPrice);
            }
            
            setValue('educationEffects', order.educationEffects, { shouldValidate: true });
            setEducationEffects(order.educationEffects?.split(';') || []);
            // Store original education effects for change detection
            setOriginalEducationEffects(order.educationEffects);
            setIsLoading(false); // Order data loaded
        }
    }, [order, reset, isDirty, setValue, setEducationEffects]);

    // Load data from copied order (copyFromOrder)
    useEffect(() => {
        if (copyFromOrder && !order) {
            const rows = (copyFromOrder.benefitients ?? '')
                .split(';')
                .filter(item => item.trim() !== '') // Filter out empty strings
                .map(item => {
                    const [firstName, lastName, pesel, birthPlace, birthDate] = item.split('~');
                    // Resetuj certId dla każdego uczestnika (nowe zamówienie = nowe certyfikaty)
                    return { firstName, lastName, pesel, birthPlace, birthDate, certId: '' };
                });
            
            reset({
                serviceId: copyFromOrder.serviceId,
                customeClientServiceName: copyFromOrder.customeClientServiceName || '',
                preposedDateTime: '', // Użytkownik musi podać nową datę
                placeOfServiceAndAnotherInformations: copyFromOrder.placeOfServiceAndAnotherInformations || '',
                payerInstitutionName: copyFromOrder.payerInstitutionName || '',
                payerLastName: copyFromOrder.payerLastName || '',
                payerFirstName: copyFromOrder.payerFirstName || '',
                payerStreet: copyFromOrder.payerStreet || '',
                payerBuilding: copyFromOrder.payerBuilding || '',
                payerZip: copyFromOrder.payerZip || '',
                payerCity: copyFromOrder.payerCity || '',
                payerState: copyFromOrder.payerState || '',
                payerCountry: copyFromOrder.payerCountry || '',
                payerNIP: copyFromOrder.payerNIP || '',
                rows,
                totalItem: rows.length || 1,
                educationEffects: copyFromOrder.educationEffects || '',
                trainingProgram: copyFromOrder.trainingProgram || '',
                unitDiscountedPrice: copyFromOrder.unitDiscountedPrice || 0,
                paymentOrderState: PaymentOrderState.PENDING, // Nowe zamówienie - status płatności na oczekujące
                generateAITest: false,
            });
            
            if (copyFromOrder.educationEffects) {
                setValue('educationEffects', copyFromOrder.educationEffects, { shouldValidate: true });
                setEducationEffects(copyFromOrder.educationEffects.split(';') || []);
            }
            
            // Ustaw cenę z kopiowanego zamówienia
            if (copyFromOrder.unitDiscountedPrice && copyFromOrder.unitDiscountedPrice > 0) {
                setUnitDiscountedPrice(copyFromOrder.unitDiscountedPrice);
            } else if (copyFromOrder.serviceId) {
                // Jeśli brak ceny w zamówieniu, pobierz z usługi
                agent.Services.price(copyFromOrder.serviceId)
                    .then(response => setUnitDiscountedPrice(response))
                    .catch(error => console.error('Błąd podczas pobierania ceny usługi:', error));
            }
            setIsLoading(false); // Copy from order data loaded
        }
    }, [copyFromOrder, order, reset, setValue, setEducationEffects]);

    // Show validation errors
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const errorMessages: string[] = [];
            const seenErrors = new Set<string>(); // Aby uniknąć zdublowanych komunikatów
            
            // Funkcja pomocnicza do rekurencyjnego zbierania błędów
            const extractErrors = (obj: any, path: string = '') => {
                if (!obj) return;
                
                if (Array.isArray(obj)) {
                    // Jeśli to tablica (np. rows)
                    obj.forEach((item, index) => {
                        if (item && typeof item === 'object') {
                            Object.entries(item).forEach(([, value]: [string, any]) => {
                                if (value?.message) {
                                    let errorMsg = '';
                                    if (path.includes('rows')) {
                                        // Formatuj błędy z beneficjentów - tylko wiadomość bez nazwy pola
                                        errorMsg = `Uczestnik ${index + 1} - ${value.message}`;
                                    } else {
                                        errorMsg = value.message;
                                    }
                                    
                                    // Dodaj tylko jeśli jeszcze nie ma tego błędu
                                    if (!seenErrors.has(errorMsg)) {
                                        seenErrors.add(errorMsg);
                                        errorMessages.push(errorMsg);
                                    }
                                }
                            });
                        }
                    });
                } else if (typeof obj === 'object') {
                    // Jeśli to obiekt
                    Object.entries(obj).forEach(([key, value]: [string, any]) => {
                        if (value?.message) {
                            if (!seenErrors.has(value.message)) {
                                seenErrors.add(value.message);
                                errorMessages.push(value.message);
                            }
                        } else if (key === 'rows' && Array.isArray(value)) {
                            extractErrors(value, 'rows');
                        }
                    });
                }
            };
            
            extractErrors(errors);
            
            // Wyświetl maksymalnie 3 błędy, aby nie zalegały komunikaty
            const messagesToShow = errorMessages.slice(0, 3);
            if (messagesToShow.length > 0) {
                const suffix = errorMessages.length > 3 ? '...' : '';
                toast.error(`Sprawdź formularz: ${messagesToShow.join(', ')}${suffix}`);
            }
        }
    }, [errors]);

    // Handle form submission with Admin-specific logic
    async function handleSubmitData(data: OrderFormValues) {
        if (!data.serviceId) {
            toast.error('Wybierz egzamin przed zapisaniem formularza');
            return;
        }

        try {
            // Calculate benefitients with potential certId for admin functionality
            data.benefitients = data.rows.map(row =>
                `${row.firstName}~${row.lastName}~${row.pesel}~${row.birthPlace}~${row.birthDate}${row.certId == null || row.certId == undefined ? '' : '~' + row.certId
                }`
            ).join(';');

            // Always set totalItem based on actual rows count
            data.totalItem = data.rows.length;
            
            // Ustaw cenę - użyj wartości ze śledzenia formularza (watchedUnitDiscountedPrice)
            // lub ze state (unitDiscountedPrice z usługi)
            // Priorytet: watchedUnitDiscountedPrice > unitDiscountedPrice (state) > 0
            let finalPrice = 0;
            if (typeof watchedUnitDiscountedPrice === 'number' && !isNaN(watchedUnitDiscountedPrice)) {
                finalPrice = watchedUnitDiscountedPrice;
            } else if (unitDiscountedPrice > 0) {
                finalPrice = unitDiscountedPrice;
            }
            data.unitDiscountedPrice = finalPrice;

            // Include generateAITest flag for SuperAdmin
            // Works for both new orders and editing (when education effects changed)
            if (isSuperAdmin) {
                data.generateAITest = generateAITest && canGenerateAI;
            } else {
                data.generateAITest = false;
            }

            let response: Order;
            if (order) {
                // Updating existing order
                response = await agent.Admin.Orders.update(data);
                toast.success('Uaktualniono zamówienie');

                // Show AI generation toast when regenerating questions due to changed effects
                if (isSuperAdmin && data.generateAITest && educationEffectsChanged) {
                    toast.info('Pytania testowe zostaną wygenerowane ponownie w tle.', {
                        autoClose: 5000
                    });
                }

                // Fetch updated data only for existing orders
                const updatedOrder = await agent.Admin.Orders.details(response.publicId);
                if (updatedOrder) {
                    reset({
                        ...updatedOrder,
                        rows: updatedOrder.benefitients
                            .split(';')
                            .filter((item: string) => item.trim() !== '') // Filter out empty strings
                            .map((item: string) => {
                                const [firstName, lastName, pesel, birthPlace, birthDate, certId] = item.split('~');
                                return { firstName, lastName, pesel, birthPlace, birthDate, certId };
                            })
                    });
                    setValue('educationEffects', updatedOrder.educationEffects, { shouldValidate: true });
                    setEducationEffects(updatedOrder.educationEffects?.split(';') || []);
                }
            } else {
                // Creating new order
                response = await agent.Admin.Orders.create(data);
                toast.success('Utworzono zamówienie');

                // Show AI generation toast only for SuperAdmin when checkbox was checked
                if (isSuperAdmin && data.generateAITest) {
                    toast.info('Pytania testowe zostaną wygenerowane w tle.', {
                        autoClose: 5000
                    });
                }

                // Clear draft when creating new order successfully
                clearDraft();
                setDraftDeleteCountdown(0);
            }

            // Admin-specific: update both admin and client stores
            dispatch(setAdminOrder(response));
            dispatch(setClientOrder(response));

            // Close form
            cancelEdit();

        } catch (error) {
            console.log(error);
            toast.error('Wystąpił błąd podczas zapisywania zamówienia');
        }
    }

    // Fill with test data (admin only)
    const fillWithTestData = () => {
        const testData: OrderFormValues = {
            serviceId: 1,
            serviceName: 'Testowy serviceName',
            customeClientServiceName: 'Testowy Egzamin',
            preposedDateTime: dayjs().toISOString(),
            placeOfServiceAndAnotherInformations: 'Testowe miejsce',
            clientComment: 'Testowy komentarz',
            trainingProgram: 'Testowy program szkoleniowy',
            educationEffects: 'Efekt1;Efekt2;Efekt3',
            payerClientName: 'Testowa Instytucja',
            payerClientAddress: 'Adres',
            payerClientNIP: 'NIP',
            payerInstitutionName: 'Testowa Instytucja',
            payerLastName: 'Kowalski',
            payerFirstName: 'Jan',
            payerStreet: 'Testowa 1',
            payerBuilding: '1',
            payerPremises: '2',
            payerZip: '00-001',
            payerCity: 'Warszawa',
            payerState: states[0],
            payerCountry: 'Polska',
            payerNIP: '1234567890',
            rows: [
                {
                    firstName: 'Jan',
                    lastName: 'Kowalski',
                    pesel: '12345678901',
                    birthPlace: 'Warszawa',
                    birthDate: '01.01.1990'
                },
                {
                    firstName: 'Anna',
                    lastName: 'Nowak',
                    pesel: '09876543210',
                    birthPlace: 'Kraków',
                    birthDate: '02.02.1985'
                }
            ],
            totalItem: 2,
            paymentOrderState: PaymentOrderState.PENDING,
            generateAITest: false
        };
        reset(testData);
        setEducationEffects(testData.educationEffects?.split(';') || []);
    };

    // Handlers for draft restore dialog
    const handleRestoreDialogClose = () => {
        setShowDraftRestoreDialog(false);
        // Reset the countdown timer when the dialog is closed without restoring
        if (storedDraft) {
            resetCountdown(); // Start 60-second countdown to delete the draft
        }
    };

    const handleRestoreDraftFromDialog = () => {
        if (storedDraft) {
            handleRestoreDraft(storedDraft);
            setShowDraftRestoreDialog(false);
        }
    };

    const handleDeleteDraft = () => {
        clearDraft();
        setShowDraftRestoreDialog(false);
    };

    const handleDraftRestoreButtonClick = () => {
        setShowDraftRestoreDialog(true);
        // Reset countdown when user clicks the button to re-open dialog
        resetCountdown();
    };

    return (
        <>
            <AppBreadcrumbs />
            <AppReturn title={"Tworzenie/edycja zamówienia"} cancelEdit={handleCancel} />
            <Box component={Paper} sx={{ p: 4 }}>
                {/* Show the restore button floating on the side if there's a draft and we're not editing */}
                {!order && storedDraft && draftDeleteCountdown > 0 && (
                    <DraftRestoreButton
                        formType={formType}
                        formId={formId}
                        draft={storedDraft}
                        onRestore={handleDraftRestoreButtonClick}
                        countdown={draftDeleteCountdown}
                    />
                )}

                <form onSubmit={handleSubmit(handleSubmitData)}>
                    {isLoading ? (
                        <Box sx={{ p: 4 }}>
                            <Stack spacing={3}>
                                <Skeleton variant="text" height={40} />
                                <Stack spacing={2}>
                                    <Skeleton variant="rounded" height={60} />
                                    <Skeleton variant="rounded" height={60} />
                                    <Skeleton variant="rounded" height={60} />
                                </Stack>
                                <Skeleton variant="rounded" height={200} />
                                <Stack spacing={1}>
                                    <Skeleton variant="text" height={30} width="60%" />
                                    <Skeleton variant="rounded" height={60} />
                                    <Skeleton variant="rounded" height={60} />
                                    <Skeleton variant="rounded" height={60} />
                                </Stack>
                                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                    <Skeleton variant="rounded" height={40} width={100} />
                                    <Skeleton variant="rounded" height={40} width={100} />
                                </Stack>
                            </Stack>
                        </Box>
                    ) : (
                        <>
                            <Box display='flex' justifyContent='center'>
                                <Button onClick={fillWithTestData} variant='outlined' color='secondary'>Wypełnij Danymi</Button>
                            </Box>

                            <OrderFormBasicInformation
                                order={order}
                                control={control}
                                possibleServices={possibleServices}
                                watch={watch}
                                setValue={setValue}
                                handleOpenModal={handleOpenModal}
                                setManualServiceChange={() => setManualServiceChange(true)}
                                isAdmin={true}
                                setEducationEffects={setEducationEffects}
                            />

                    <OrderFormPayerData control={control} setValue={setValue} />
                    <OrderFormBenefitients
                        control={control as unknown as Control<FieldValues>}
                        fields={fields}
                        append={append as unknown as (value: FieldValues | FieldValues[]) => void}
                        remove={remove}
                        handleSetTotalItemRows={handleSetTotalItemRows}
                        setValue={setValue}
                        watch={watch}
                    />
                    
                    {/* Price Field - Admin can edit price per participant */}
                    <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>Cena za uczestnika</Typography>
                    <Grid container spacing={3} marginBottom={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <AppNumberInput control={control} name='unitDiscountedPrice' label='Cena za osobę' defaultValue={0} />
                        </Grid>
                    </Grid>

                    <OrderValueBox sum={sum} unitDiscountedPrice={unitDiscountedPrice} totalItem={totalItemRows} />

                    {/* AI Test Generation Checkbox - SuperAdmin only */}
                    {isSuperAdmin && (
                        <Box sx={{ mt: 1, display: 'inline-flex', flexDirection: 'column' }}>
                            <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Tooltip title={aiCheckboxState.tooltip} arrow placement="right">
                                    <span>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={generateAITest && canGenerateAI}
                                                    onChange={(e) => setValue('generateAITest', e.target.checked, { shouldDirty: true })}
                                                    disabled={aiCheckboxState.disabled}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Typography 
                                                    variant="body2" 
                                                    color={aiCheckboxState.disabled ? 'text.disabled' : 'text.primary'}
                                                >
                                                    {order && orderHasQuestions ? 'Generuj ponownie test AI' : 'Generuj test AI'}
                                                </Typography>
                                            }
                                            sx={{ m: 0 }}
                                        />
                                    </span>
                                </Tooltip>
                                {order && !orderHasQuestions && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                        Zamówienie nie ma jeszcze pytań - możesz wygenerować test AI.
                                    </Typography>
                                )}
                                {order && orderHasQuestions && !educationEffectsChanged && (
                                    <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                                        Zmień efekty kształcenia, aby móc wygenerować nowe pytania.
                                    </Typography>
                                )}
                                {order && educationEffectsChanged && (
                                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                        Efekty kształcenia zmienione - zaznacz, aby wygenerować nowe pytania.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {!isLoading && (
                        <>
                            <Box display='flex' justifyContent='space-between' sx={{ mt: 3 }}>
                                <Button onClick={handleCancel} variant='outlined' color='primary'>Anuluj</Button>
                                <LoadingButton loading={isSubmitting} disabled={!isDirty} type='submit' variant='outlined' color='success'>Zapisz</LoadingButton>
                            </Box>

                            <OrderFormEducationEffectsModal
                                openModal={openModal}
                                handleCloseModal={handleCloseModal}
                                educationEffects={educationEffects}
                                updateEffect={updateEffect}
                                removeEffect={removeEffect}
                                addNewEffect={addNewEffect}
                                handleSaveEducationEffects={handleSaveEducationEffects}
                            />
                        </>
                    )}
                        </>
                    )}
                </form>
            </Box>

            {/* Regular unsaved changes dialog */}
            <AppUnsavedChangesDialog
                open={showUnsavedChangesDialog}
                onClose={handleCancelNavigation}
                onConfirm={handleConfirmNavigation}
            />

            {/* Draft restore dialog */}
            <DraftRestoreDialog
                open={showDraftRestoreDialog}
                onClose={handleRestoreDialogClose}
                onRestore={handleRestoreDraftFromDialog}
                onDelete={handleDeleteDraft}
                draftSummary={draftSummary}
            />
        </>
    );
}